import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAnalysisResults, triggerApkAnalysis as startAnalysis } from '../services/analysisUIService';
import { onAuthStateChange } from '../auth';
import { db } from '../config/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import Button from './Button';

export default function AnalysisProgress({ inlineAnalysis = false, analysisData = null, onViewDetailedResults = null }) {
  const { fileId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [showResults, setShowResults] = useState(false);

  // Check authentication status - only if not in inline mode
  useEffect(() => {
    if (inlineAnalysis) {
      setIsLoggedIn(true);
      return () => {};
    }
    
    const unsubscribe = onAuthStateChange((user) => {
      setIsLoggedIn(!!user);
      // Don't redirect non-logged in users - allow anonymous access to analysis results
    });
    return () => unsubscribe();
  }, [navigate, inlineAnalysis]);

  // If in inline mode, use the analysis data passed as props
  useEffect(() => {
    if (inlineAnalysis && analysisData) {
      setAnalysis(analysisData);
      setLoading(false);
      setError('');
      setLastUpdate(new Date());
      
      // Check for error state
      if (analysisData.status === 'error') {
        setError(analysisData.error || 'Analysis encountered an error');
      }
    }
  }, [inlineAnalysis, analysisData]);

  // Subscribe to real-time updates for this analysis - only if not in inline mode
  useEffect(() => {
    if (inlineAnalysis || !fileId) return;

    console.log(`Analysis component: subscribing to updates for fileId: ${fileId}`);
    
    setLoading(true);
    setError('');

    // Set up a real-time listener for the analysis document
    const analysisRef = doc(db, 'apkAnalysisUIProgress', fileId);
    const unsubscribe = onSnapshot(
      analysisRef,
      (doc) => {
        setLoading(false);
        if (doc.exists()) {
          console.log(`Analysis document data:`, doc.data());
          const analysisData = { id: doc.id, ...doc.data() };
          
          // Track when the document was last updated
          setLastUpdate(new Date());
          
          // Update analysis state
          setAnalysis(analysisData);
          
          // Check for error state
          if (analysisData.status === 'error') {
            setError(analysisData.error || 'Analysis encountered an error');
          } else {
            setError('');
          }
        } else {
          console.log(`Analysis document not found for fileId: ${fileId}`);
          setError('Analysis not found');
        }
      },
      (err) => {
        console.error('Error getting real-time updates:', err);
        setLoading(false);
        setError(`Failed to get analysis updates: ${err.message}`);
      }
    );

    // Clean up the subscription
    return () => unsubscribe();
  }, [fileId, inlineAnalysis]);

  // Check if analysis is stalled
  const isAnalysisStalled = () => {
    if (!analysis) return false;
    
    // Consider analysis stalled if status is processing/pending and hasn't been updated in 1 minute
    const oneMinuteAgo = Date.now() - 60000;
    const lastUpdateTime = analysis.updatedAt ? 
      (analysis.updatedAt.seconds * 1000) : // Convert Firestore timestamp to ms
      (lastUpdate ? lastUpdate.getTime() : Date.now());
      
    return (analysis.status === 'processing' || analysis.status === 'pending') && 
           (lastUpdateTime < oneMinuteAgo) &&
           analysis.progress < 100;
  };

  if (!isLoggedIn && !inlineAnalysis) {
    // Allow anonymous users to view the analysis instead of returning null (which would lead to redirect)
    // Just continue with the component rendering
    console.log("Anonymous user viewing analysis results");
  }

  // Display progress bar based on analysis progress
  const renderProgressBar = () => {
    // Default to 0 if progress is undefined
    const currentProgress = analysis && typeof analysis.progress === 'number' ? analysis.progress : 0;
    const progressLabel = analysis && analysis.label ? analysis.label : 'Processing';
    
    return (
      <div className={`bg-white rounded-lg p-4 shadow-sm ${inlineAnalysis ? 'bg-opacity-10' : 'border border-gray-100'}`}>
        <div className="flex justify-between items-center mb-3">
          <span className={`text-base font-medium ${inlineAnalysis ? 'text-white' : 'text-gray-700'}`}>{progressLabel}</span>
          <span className={`text-sm font-medium ${inlineAnalysis ? 'text-gray-300' : 'text-gray-500'}`}>{currentProgress}%</span>
        </div>
        <div className={`w-full ${inlineAnalysis ? 'bg-gray-700' : 'bg-gray-100'} rounded-full h-3 overflow-hidden`}>
          <div 
            className={`h-full rounded-full transition-all duration-500 ease-out ${
              isAnalysisStalled() 
                ? 'bg-yellow-500 animate-pulse' 
                : 'bg-blue-500'
            }`}
            style={{ width: `${currentProgress}%` }}
          />
        </div>
        {isAnalysisStalled() && (
          <p className="text-yellow-600 text-sm mt-2 flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Analysis is taking longer than expected...
          </p>
        )}
      </div>
    );
  };

  // Display current analysis step
  const renderCurrentAction = () => {
    if (!analysis || analysis.progress === 100) return null;
    
    const currentLabel = analysis.label || "Processing...";
    const currentProgress = analysis.progress || 0;
    
    // Define analysis steps based on the order in functions/src/index.ts
    const analysisSteps = [
      { label: "Uploading app file", progress: 5 },
      { label: "Analyzing file size", progress: 15 },
      { label: "Reading app manifest", progress: 30 },
      { label: "Extracting permissions", progress: 45 },
      { label: "Evaluating security risks", progress: 60 },
      { label: "Mapping permissions to categories", progress: 75 },
      { label: "Building report", progress: 90 },
      { label: "Analysis complete", progress: 100 }
    ];
    
    // Find the current step index based on current progress
    const currentStepIndex = analysisSteps.findIndex(step => 
      step.label === currentLabel || (step.progress > currentProgress && currentProgress < 100)
    );

    // Only show completed steps and current step (not future steps)
    const relevantSteps = analysisSteps.slice(0, currentStepIndex + 1);
    
    // Show one step ahead (upcoming) if not at the end
    if (currentStepIndex < analysisSteps.length - 1) {
      relevantSteps.push(analysisSteps[currentStepIndex + 1]);
    }
    
    return (
      <div className={`rounded-lg p-4 border ${inlineAnalysis ? 'bg-blue-900/30 border-blue-800' : 'bg-blue-50 border-blue-100'}`}>
        <div className="flex items-center mb-4">
          <h3 className={`font-medium text-lg ${inlineAnalysis ? 'text-blue-300' : 'text-blue-800'}`}>Analysis Progress</h3>
        </div>
        
        <div className="space-y-4">
          {relevantSteps.map((step, index) => {
            const absoluteIndex = analysisSteps.findIndex(s => s.label === step.label);
            
            // Determine status of this step
            let status;
            if (absoluteIndex < currentStepIndex) {
              status = "completed"; // Previous step
            } else if (absoluteIndex === currentStepIndex) {
              status = "current"; // Current step
            } else {
              status = "pending"; // Future step (only one ahead is shown)
            }

            // Apply different animations based on step status
            const animationClass = 
              status === "current" ? "animate-slide-in-right" :
              status === "pending" ? "animate-fade-in-slow" : "";
            
            return (
              <div key={absoluteIndex} className={animationClass}>
                <div className="flex items-center">
                  <div className={`h-8 w-8 flex items-center justify-center rounded-full 
                    ${status === "completed" 
                      ? (inlineAnalysis ? 'bg-green-600/70' : 'bg-green-500') 
                      : status === "current"
                        ? (inlineAnalysis ? 'bg-blue-600/70' : 'bg-blue-500')
                        : (inlineAnalysis ? 'bg-gray-700' : 'bg-gray-300')
                    } text-white`}>
                    {status === "completed" ? (
                      <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : status === "current" ? (
                      <div className="h-4 w-4 border-t-2 border-r-2 border-white rounded-full animate-spin"></div>
                    ) : (
                      <span>{absoluteIndex + 1}</span>
                    )}
                  </div>
                  <div className="ml-3">
                    <p className={`font-medium ${
                      status === "completed" 
                        ? (inlineAnalysis ? 'text-green-300' : 'text-green-700') 
                        : status === "current"
                          ? (inlineAnalysis ? 'text-blue-300' : 'text-blue-700')
                          : (inlineAnalysis ? 'text-gray-400' : 'text-gray-500')
                    }`}>
                      {step.label}
                    </p>
                  </div>
                </div>
                
                {/* Connector line between steps (except last) with animation */}
                {index < relevantSteps.length - 1 && (
                  <div className={`w-0.5 h-4 ml-4 ${
                    status === "current" ? "animate-grow-down" : ""
                  } ${inlineAnalysis ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
                )}
              </div>
            );
          })}
          
          {/* Show indicator that there are more steps if not showing all */}
          {currentStepIndex < analysisSteps.length - 2 && (
            <div className="flex items-center animate-fade-in">
              <div className={`w-0.5 h-4 ${inlineAnalysis ? 'bg-gray-700' : 'bg-gray-200'} ml-4`}></div>
              <div className="ml-3 mt-1">
                <div className="flex space-x-1">
                  <div className={`w-1 h-1 rounded-full ${inlineAnalysis ? 'bg-gray-400' : 'bg-gray-400'} animate-pulse-delayed-1`}></div>
                  <div className={`w-1 h-1 rounded-full ${inlineAnalysis ? 'bg-gray-400' : 'bg-gray-400'} animate-pulse-delayed-2`}></div>
                  <div className={`w-1 h-1 rounded-full ${inlineAnalysis ? 'bg-gray-400' : 'bg-gray-400'} animate-pulse-delayed-3`}></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const getStatusBadge = (analysis) => {
    if (analysis.status === 'error') {
      return (
        <span className={`px-2 py-1 md:px-3 md:py-1 rounded text-xs md:text-sm font-medium ${inlineAnalysis ? 'bg-red-500/50 text-red-100' : 'bg-red-100 text-red-800'}`}>
          Error
        </span>
      );
    } else if (analysis.progress === 100) {
      return (
        <span className={`px-2 py-1 md:px-3 md:py-1 rounded text-xs md:text-sm font-medium ${inlineAnalysis ? 'bg-green-500/50 text-green-100' : 'bg-green-100 text-green-800'}`}>
          Completed
        </span>
      );
    } else if (analysis.status === 'pending') {
      return (
        <span className={`px-2 py-1 md:px-3 md:py-1 rounded text-xs md:text-sm font-medium ${inlineAnalysis ? 'bg-yellow-500/50 text-yellow-100' : 'bg-yellow-100 text-yellow-800'}`}>
          Pending
        </span>
      );
    } else if (analysis.status === 'processing') {
      return (
        <span className={`px-2 py-1 md:px-3 md:py-1 rounded text-xs md:text-sm font-medium ${inlineAnalysis ? 'bg-blue-500/50 text-blue-100' : 'bg-blue-100 text-blue-800'}`}>
          Processing
        </span>
      );
    } else {
      return (
        <span className={`px-2 py-1 md:px-3 md:py-1 rounded text-xs md:text-sm font-medium ${inlineAnalysis ? 'bg-red-500/50 text-red-100' : 'bg-red-100 text-red-800'}`}>
          Failed
        </span>
      );
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    // Handle Firestore timestamp objects
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString();
    }
    
    // Handle Date objects and numbers
    if (timestamp instanceof Date) {
      return timestamp.toLocaleString();
    }
    
    if (typeof timestamp === 'number') {
      return new Date(timestamp).toLocaleString();
    }
    
    return 'Processing...';
  };

  const triggerAnalysisRetry = async () => {
    setError('');
    try {
      if (inlineAnalysis) {
        // In inline mode, just call the retry function from the parent
        const actualFileId = analysis?.id || analysisData?.fileId;
        if (actualFileId) {
          await startAnalysis(actualFileId);
        } else {
          setError('Cannot restart analysis: file ID not found');
        }
      } else {
        // Normal mode, use the fileId from the URL
        await startAnalysis(fileId);
      }
      setError('');
    } catch (error) {
      setError(`Failed to restart analysis: ${error.message}`);
    }
  };

  // When to view detailed results
  const handleViewDetailedResults = () => {
    // If custom handler is provided, use it
    if (onViewDetailedResults && typeof onViewDetailedResults === 'function') {
      onViewDetailedResults();
      return;
    }
    
    // Otherwise navigate to dashboard instead of detailed results page
    // Always navigate to dashboard, even for anonymous users
    navigate('/dashboard');
  };

  // Use the appropriate container style based on whether we're inline or not
  const containerClass = inlineAnalysis 
    ? "analysis-container" 
    : "analysis-container p-4 md:p-6 max-w-4xl mx-auto";

  // Use the appropriate title based on whether we're inline or not
  const titleElement = !inlineAnalysis && (
    <h1 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6 text-left">App Analysis Dashboard</h1>
  );

  return (
    <div className={containerClass}>
      {titleElement}
      
      {loading && (
        <div className="loading-state text-center py-6 md:py-8 animate-fade-in">
          <div className="w-12 h-12 md:w-16 md:h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3 md:mb-4"></div>
          <p className={`text-lg md:text-xl ${inlineAnalysis ? 'text-white' : ''}`}>Loading analysis data...</p>
        </div>
      )}
      
      {error && (
        <div className={`error-message px-3 py-2 md:px-4 md:py-3 rounded mb-4 text-sm md:text-base animate-shake ${inlineAnalysis ? 'bg-red-900/50 border border-red-800 text-red-100' : 'bg-red-100 border border-red-400 text-red-700'}`}>
          {error}
        </div>
      )}
      
      {!loading && !error && analysis && (
        <div className={`analysis-result rounded-lg overflow-hidden animate-slide-up ${inlineAnalysis ? 'bg-transparent' : 'bg-white shadow-lg'}`}>
          <div className="p-4 md:p-6">
            {/* Header Section */}
            <div className={`mb-6 pb-4 border-b ${inlineAnalysis ? 'border-gray-600' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between mb-4">
                <h2 className={`text-xl md:text-2xl font-semibold ${inlineAnalysis ? 'text-white' : 'text-gray-800'}`}>{analysis.fileName || 'App File'}</h2>
                <div className="transform hover:scale-105 transition-transform">
                  {getStatusBadge(analysis)}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                <div className={`p-3 rounded-lg ${inlineAnalysis ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <p className={`text-sm ${inlineAnalysis ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Uploaded</p>
                  <p className={`font-medium ${inlineAnalysis ? 'text-gray-200' : 'text-gray-700'}`}>{formatTimestamp(analysis.submittedAt)}</p>
                </div>
                {analysis.updatedAt && (
                  <div className={`p-3 rounded-lg ${inlineAnalysis ? 'bg-gray-800' : 'bg-gray-50'}`}>
                    <p className={`text-sm ${inlineAnalysis ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Last Updated</p>
                    <p className={`font-medium ${inlineAnalysis ? 'text-gray-200' : 'text-gray-700'}`}>{formatTimestamp(analysis.updatedAt)}</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Progress Section */}
            <div className="mb-6">
              {renderProgressBar()}
            </div>
            
            {/* Current Action Section */}
            {analysis.status === 'processing' && (
              <div className="mb-6 animate-fade-in">
                {renderCurrentAction()}
              </div>
            )}
            
            {/* Results or Status Section */}
            {analysis.progress === 100 && analysis.results ? (
              <div className="analysis-details animate-fade-in">
                <div className="text-center py-6">
                  <div className="mb-4">
                    <div className={`inline-flex items-center justify-center h-16 w-16 md:h-24 md:w-24 rounded-full mb-4 ${inlineAnalysis ? 'bg-green-900/50' : 'bg-green-100'}`}>
                      <svg className={`h-8 w-8 md:h-10 md:w-10 ${inlineAnalysis ? 'text-green-300' : 'text-green-600'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className={`text-xl md:text-2xl font-semibold mb-2 ${inlineAnalysis ? 'text-white' : 'text-gray-800'}`}>Analysis Complete!</h3>
                    <p className={`mb-6 ${inlineAnalysis ? 'text-gray-300' : 'text-gray-600'}`}>Your app has been successfully analyzed and the results are ready.</p>
                  </div>
                  
                  <Button 
                    onClick={handleViewDetailedResults}
                    icon={
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    }
                    className="mx-auto"
                  >
                    View App Details
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-left">
                {analysis.status === 'error' && (
                  <p className={`text-lg md:text-xl ${inlineAnalysis ? 'text-red-300' : 'text-gray-700'}`}>
                    Analysis failed. Please try again.
                  </p>
                )}
                
                {/* Retry Button */}
                {(isAnalysisStalled() || analysis.status === 'error') && (
                  <Button 
                    onClick={triggerAnalysisRetry}
                    className="mt-4"
                  >
                    Retry Analysis
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.5s ease-out forwards;
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
        @keyframes fade-in-slow {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in-slow {
          animation: fade-in-slow 0.6s ease-out forwards;
        }
        @keyframes slide-in-right {
          from { opacity: 0; transform: translateX(-10px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.4s ease-out forwards;
        }
        @keyframes grow-down {
          from { height: 0; opacity: 0; }
          to { height: 16px; opacity: 1; }
        }
        .animate-grow-down {
          animation: grow-down 0.3s ease-out forwards;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
        @keyframes pulse-delayed-1 {
          0%, 20%, 100% { opacity: 0.3; }
          50%, 70% { opacity: 1; }
        }
        .animate-pulse-delayed-1 {
          animation: pulse-delayed-1 1.5s infinite;
        }
        @keyframes pulse-delayed-2 {
          0%, 40%, 100% { opacity: 0.3; }
          70%, 90% { opacity: 1; }
        }
        .animate-pulse-delayed-2 {
          animation: pulse-delayed-2 1.5s infinite;
        }
        @keyframes pulse-delayed-3 {
          0%, 60%, 100% { opacity: 0.3; }
          90%, 10% { opacity: 1; }
        }
        .animate-pulse-delayed-3 {
          animation: pulse-delayed-3 1.5s infinite;
        }
      `}</style>
    </div>
  );
} 